// Shared constants for the fabrication order acceptance status shown in the acceptance list (CU4).
// Source of truth for status/selectability lives in Sun (RN7, S1/S2/S14/S15); this module only
// maps the 4 statuses returned by the read endpoint to their i18n label key and their MUI Chip
// color/variant, following the same "colored chip = status" convention used elsewhere in the MIC
// (e.g. JobStatusChip): finished = success, in progress = info, pending = neutral, not available =
// muted outlined (not yet in the circuit). Colors come from the app theme, not hardcoded hex.

export const FABRICATION_ORDER_ACCEPTANCE_STATUSES = {
  NOT_AVAILABLE: 'NOT_AVAILABLE',
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  // #70 r3 — a version that was started and then paused with "Interrupt". Distinct from PENDING (a
  // fresh version never started): a PENDING version offers "Start acceptance", an INTERRUPTED one
  // offers "Resume acceptance". Neither is editable — only IN_PROGRESS is.
  INTERRUPTED: 'INTERRUPTED',
  FINISHED: 'FINISHED'
}

// i18n key used for each status badge label. Default English strings agreed with the story's
// impact analysis: "Not available / Pending / In progress / Interrupted / Finished".
export const FABRICATION_ORDER_ACCEPTANCE_STATUS_LABEL_KEYS = {
  [FABRICATION_ORDER_ACCEPTANCE_STATUSES.NOT_AVAILABLE]: 'Not available',
  [FABRICATION_ORDER_ACCEPTANCE_STATUSES.PENDING]: 'Pending',
  [FABRICATION_ORDER_ACCEPTANCE_STATUSES.IN_PROGRESS]: 'In progress',
  [FABRICATION_ORDER_ACCEPTANCE_STATUSES.INTERRUPTED]: 'Interrupted',
  [FABRICATION_ORDER_ACCEPTANCE_STATUSES.FINISHED]: 'Finished'
}

// MUI Chip `color`/`variant` per status — resolved against the active theme palette, so the badge
// stays consistent whether the app is rendered in light or dark mode.
const STATUS_CHIP_PROPS = {
  [FABRICATION_ORDER_ACCEPTANCE_STATUSES.FINISHED]: { color: 'success', variant: 'filled' },
  [FABRICATION_ORDER_ACCEPTANCE_STATUSES.IN_PROGRESS]: { color: 'info', variant: 'filled' },
  [FABRICATION_ORDER_ACCEPTANCE_STATUSES.PENDING]: { color: 'default', variant: 'filled' },
  // #70 r3 — a paused version: warning + outlined, visibly distinct from the neutral PENDING chip so
  // "started-then-paused" reads apart from "never started".
  [FABRICATION_ORDER_ACCEPTANCE_STATUSES.INTERRUPTED]: { color: 'warning', variant: 'outlined' },
  // Added treatment (absent in the original prototype): muted, outlined — reads as "not yet in the
  // circuit".
  [FABRICATION_ORDER_ACCEPTANCE_STATUSES.NOT_AVAILABLE]: { color: 'default', variant: 'outlined' }
}

// #70 — acceptance result, fixed manually by the responsible when closing a version (RN-F). Present
// if and only if the version is FINISHED (INV2). Source of truth is Sun; here we only map the enum
// to its i18n label and its Chip color (passed = success, failed = error).
export const FABRICATION_ORDER_ACCEPTANCE_RESULTS = {
  PASSED: 'PASSED',
  FAILED: 'FAILED'
}

const RESULT_LABEL_KEYS = {
  [FABRICATION_ORDER_ACCEPTANCE_RESULTS.PASSED]: 'Passed',
  [FABRICATION_ORDER_ACCEPTANCE_RESULTS.FAILED]: 'Failed'
}

const RESULT_CHIP_PROPS = {
  [FABRICATION_ORDER_ACCEPTANCE_RESULTS.PASSED]: { color: 'success', variant: 'outlined' },
  [FABRICATION_ORDER_ACCEPTANCE_RESULTS.FAILED]: { color: 'error', variant: 'outlined' }
}

export const getStatusLabelKey = (status) => FABRICATION_ORDER_ACCEPTANCE_STATUS_LABEL_KEYS[status] || status

export const getStatusChipProps = (status) =>
  STATUS_CHIP_PROPS[status] || STATUS_CHIP_PROPS[FABRICATION_ORDER_ACCEPTANCE_STATUSES.NOT_AVAILABLE]

// Returns the i18n label key for a result, or '' when there is no result yet (version not closed).
export const getResultLabelKey = (result) => RESULT_LABEL_KEYS[result] || ''

export const getResultChipProps = (result) => RESULT_CHIP_PROPS[result] || { color: 'default', variant: 'outlined' }
